import type { TSailingDataResponse } from '@/infra/types/voyageInfo/sailing';

import PortsSummary from '@/components/ChooseVoyage/FullCruisePortsOfCall/PortsSummary';
import Image from '@/components/Image';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/Tabs';
import UIResource from '@/components/UIResource';
import { selectSailingData } from '@/ducks/voyageInfo/sailing/selectors';
import { decodeInnerHTML } from '@/helpers/util';
import { useAppSelector } from '@/store';

import ItineraryList from './ItineraryList';

import './FullCruisePortsOfCall.scss';

type TFullCruisePortsOfCallProp = {
  children?: React.ReactNode;
  isHeadingShown?: boolean;
  onItemClick?: () => unknown;
  showItineraryToggle?: boolean;
  tabDefaultValue: 'day-by-day' | 'ports';
};

const FullCruisePortsOfCall = ({
  children,
  isHeadingShown = true,
  onItemClick = () => {},
  showItineraryToggle = false,
  tabDefaultValue = 'ports',
}: TFullCruisePortsOfCallProp) => {
  const sailingData = useAppSelector(selectSailingData) as TSailingDataResponse;
  const { activitiesHeading, description, duration, itinerary, mapImage, name: sailingName } = sailingData;

  return (
    <div className="FullCruisePortsOfCall">
      {isHeadingShown && (
        <div className="headingSubHeading">
          <h2 className="sailingHeading">{activitiesHeading}</h2>
          <div className="sailingSubHeading">{decodeInnerHTML(description)}</div>
        </div>
      )}
      <div className="FullCruiseImage">
        <Image alt={mapImage?.alt} aria-hidden aspectRatio={267 / 470} className="FullCruiseImg" src={mapImage?.src} />
      </div>
      <div className="portsOfCalls">
        {isHeadingShown && (
          <div className="sailingNameShipName">
            <div className="sailingNameDuration">
              <span>{sailingName}</span>
              <span> &#8226;</span>
              {` ${duration} Nights`}
            </div>
            <div className="sailingShipName">
              <UIResource
                id="ItineraryDetail.SailingShip"
                values={{
                  Ship: sailingData?.ship?.name ?? '',
                }}
              />
            </div>
          </div>
        )}
        {children}

        <Tabs defaultValue={tabDefaultValue}>
          {showItineraryToggle && (
            <TabsList aria-label="Voyage timeline" className="filterToggle">
              <TabsTrigger value="ports">
                <UIResource id="Precheckout.timelineToggle.ports" />
              </TabsTrigger>
              <TabsTrigger value="day-by-day">
                <UIResource id="Precheckout.timelineToggle.dayByDay" />
              </TabsTrigger>
            </TabsList>
          )}
          <TabsContent
            aria-labelledby="itineraryList"
            id="itineraryList"
            preserveNativeAriaLabelledby
            role="tabpanel"
            value="day-by-day"
          >
            <ItineraryList itinerary={itinerary} onItemClick={onItemClick} />
          </TabsContent>
          <TabsContent value="ports">
            <PortsSummary sailingPortDetails={itinerary} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default FullCruisePortsOfCall;
